import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router";
import { useCallback, useMemo } from "react";
import { format, subMonths, startOfYear, endOfYear, subYears } from "date-fns";
import { OpenAPI } from "@/api/core/OpenAPI";
import { ANALYTICS_REQUEST_TIMEOUT_MS } from "@/api/requestTimeout";
import { boundedSignal } from "@/lib/requestSignal";
import { endpointKey, endpointQueryString, keepPreviousWithinOrg, type EndpointParams } from "@/lib/queryKeys";
import type {
  CockpitParams,
  CockpitResponse,
  ContractMovementResponse,
  ChildEnrollmentMovementResponse,
  PaymentMethodsResponse,
  SubsidyResponse,
  PaymentBehaviorResponse,
  LiquidityForecastResponse,
  ForecastResponse,
  ForecastParams,
  CashInResponse,
  CashInParams,
  ActivityMinutesResponse,
  CareRatioResponse,
  ViolationsResponse,
  WorkflowItemsResponse,
  WorkflowProcessingTimeResponse,
  PeriodPreset,
} from "@/types/cockpit";

// Mirror the generated client's base-URL resolution (VITE_API_BASE with a
// localhost fallback). Reading the raw env var with no fallback yields
// `undefined`, so `fetch("undefined/api/v1/...")` resolves against the page
// origin — a silent bug in prod and, in tests, a request to the default origin.
const API_BASE = OpenAPI.BASE;

/**
 * A failed cockpit fetch as an error carrying the HTTP status, like the generated
 * client's ApiError, so error states can tell "no access" (403) from a failure.
 */
export function cockpitError(status: number): Error & { status: number } {
  return Object.assign(new Error(`Cockpit API error: ${status}`), { status });
}

/**
 * One cockpit GET: aborted when React Query's signal fires (the screen left,
 * the filters changed) or after the analytics deadline (#2191 D11).
 */
export async function fetchCockpit<T>(url: string, token: string, signal: AbortSignal): Promise<T> {
  const bounded = boundedSignal(signal, ANALYTICS_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: bounded.signal,
    });
    if (!res.ok) {
      throw cockpitError(res.status);
    }
    return (await res.json()) as T;
  } finally {
    bounded.done();
  }
}

export const cockpitKeys = {
  all: ["cockpit"] as const,
  section: (section: string) => [...cockpitKeys.all, section] as const,
};

/**
 * One cockpit GET (`endpoint` is the path after `/api/v1/cockpit/`). Key and
 * URL come from the same normalised params (`lib/queryKeys.ts`), so the hub,
 * the silos and the detail share one request for one question (#2193 B4).
 */
export function useCockpitEndpoint<T>(
  endpoint: string,
  params: EndpointParams,
  options: { enabled?: boolean; retry?: number; refetchOnWindowFocus?: boolean } = {},
) {
  const { isSignedIn, isAuthReady, getToken, orgId } = useAuthContext();
  const path = `/api/v1/cockpit/${endpoint}`;
  const { enabled = true, ...rest } = options;

  return useQuery<T>({
    queryKey: endpointKey(orgId, path, params),
    queryFn: async ({ signal }) => {
      const token = await getToken();
      const query = endpointQueryString(path, params);
      return fetchCockpit<T>(`${API_BASE}${path}${query ? `?${query}` : ""}`, token, signal);
    },
    // Keep the previous period's data on screen while a new one loads so the
    // chart holds its space and can blur in place (see ChartLoadingBlur) on a
    // filter change instead of collapsing to an empty state; never across orgs.
    placeholderData: keepPreviousWithinOrg(orgId),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && isSignedIn && isAuthReady,
    ...rest,
  });
}

/** A ranged cockpit metric: runs once both months are known. */
function useRangedMetric<T>(endpoint: string, params: CockpitParams & EndpointParams) {
  return useCockpitEndpoint<T>(endpoint, params, { enabled: !!params.from && !!params.to });
}

export function useCockpitMetric(
  section: string,
  metric: string,
  params: CockpitParams,
) {
  return useRangedMetric<CockpitResponse>(`${section}/${metric}`, params);
}

export function useFinancialMetric<T>(
  endpoint: string,
  params: CockpitParams & Record<string, string | undefined>,
) {
  return useRangedMetric<T>(`financial/${endpoint}`, params);
}

export function useContractMovement(params: CockpitParams & { forecast?: string }) {
  return useFinancialMetric<ContractMovementResponse>("contracts", params);
}

/**
 * Per-CHILD enrollment movement (distinct children starting / ending enrollment).
 * Unlike `useContractMovement` (which counts contract-version rows), this counts
 * distinct children by collapsing each child's contract versions and contract
 * groups into continuous enrollment spans. Org-wide — childcare/group are not
 * sent (`GET /api/v1/cockpit/children/enrollment-movement`), so only the params
 * the endpoint reads are forwarded.
 */
export function useChildEnrollmentMovement(params: {
  from: string;
  to: string;
  forecast?: string;
}) {
  return useRangedMetric<ChildEnrollmentMovementResponse>("children/enrollment-movement", {
    from: params.from,
    to: params.to,
    forecast: params.forecast,
  });
}

export function usePaymentMethods(params: CockpitParams) {
  return useFinancialMetric<PaymentMethodsResponse>("payment-methods", params);
}

export function useSubsidyMetric(params: CockpitParams) {
  return useFinancialMetric<SubsidyResponse>("subsidies", params);
}

// Phase 4 hooks
export function usePaymentBehavior(params: CockpitParams) {
  return useFinancialMetric<PaymentBehaviorResponse>("payments", params);
}

export function useLiquidityForecast(params: CockpitParams) {
  return useFinancialMetric<LiquidityForecastResponse>("liquidity", params);
}

/**
 * Finance & Forecast metric. Unlike the other financial hooks, from/to are
 * optional — the backend derives the [first-service-year … +horizon] axis when
 * they are omitted, and a pinned range narrows it. Every param is in the query
 * key, so changing any filter or the split_by dimension re-queries.
 */
export function useRevenueForecast(
  params: ForecastParams,
  options?: { enabled?: boolean },
) {
  return useCockpitEndpoint<ForecastResponse>(
    "financial/forecast",
    {
      from: params.from,
      to: params.to,
      childcare_id: params.childcare_id,
      group_id: params.group_id,
      payer: params.payer,
      split_by: params.split_by,
      granularity: params.granularity,
      horizon_years: params.horizon_years,
      actuals_only: params.actuals_only ? true : undefined,
      target_year: params.target_year || undefined,
    },
    {
      // The forecast is the one heavy endpoint (60-month engine dry-run). Cap
      // retries at 1 (TanStack's default 3 would re-run a slow/timing-out
      // projection three times) and don't refetch on window focus, so tabbing
      // back never silently kicks off another expensive projection (P2).
      retry: 1,
      refetchOnWindowFocus: false,
      enabled: options?.enabled ?? true,
    },
  );
}

/**
 * Cash-in reconciliation browser (Phase 2): received cash bucketed at
 * year/month/week/day + a per-counterparty breakdown. Re-queries when any param
 * (incl. granularity) changes.
 */
export function useCashIn(params: CashInParams) {
  return useCockpitEndpoint<CashInResponse>(
    "financial/cashin",
    {
      from: params.from,
      to: params.to,
      childcare_id: params.childcare_id,
      group_id: params.group_id,
      payer: params.payer,
      granularity: params.granularity,
    },
    { enabled: !!params.from && !!params.to },
  );
}

export function useActivityMinutes(params: CockpitParams) {
  return useRangedMetric<ActivityMinutesResponse>("children/activities", params);
}

export function useCareRatio(params: CockpitParams) {
  return useRangedMetric<CareRatioResponse>("occupancy/care-ratio", params);
}

export function useViolations(params: CockpitParams) {
  return useRangedMetric<ViolationsResponse>("occupancy/violations", params);
}

export function useWorkflowItems(
  params: CockpitParams & { category?: string },
) {
  return useRangedMetric<WorkflowItemsResponse>("workflows/items", params);
}

export function useWorkflowProcessingTime(
  params: CockpitParams & { category?: string },
) {
  return useRangedMetric<WorkflowProcessingTimeResponse>("workflows/processing-time", params);
}

export function getPeriodRange(
  preset: PeriodPreset,
  customFrom?: string,
  customTo?: string,
): { from: string; to: string } {
  const now = new Date();

  switch (preset) {
    case "this_year": {
      const yearStart = startOfYear(now);
      const yearEnd = endOfYear(now);
      return {
        from: format(yearStart, "yyyy-MM"),
        to: format(yearEnd, "yyyy-MM"),
      };
    }
    case "last_year": {
      const lastYear = subYears(now, 1);
      const yearStart = startOfYear(lastYear);
      const yearEnd = endOfYear(lastYear);
      return {
        from: format(yearStart, "yyyy-MM"),
        to: format(yearEnd, "yyyy-MM"),
      };
    }
    case "last_12_months": {
      const from = subMonths(now, 11);
      return {
        from: format(from, "yyyy-MM"),
        to: format(now, "yyyy-MM"),
      };
    }
    case "last_24_months": {
      const from = subMonths(now, 23);
      return {
        from: format(from, "yyyy-MM"),
        to: format(now, "yyyy-MM"),
      };
    }
    case "custom":
      return {
        from: customFrom || format(startOfYear(now), "yyyy-MM"),
        to: customTo || format(now, "yyyy-MM"),
      };
  }
}

export function useCockpitParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(
    () => ({
      section: searchParams.get("section") || "",
      metric: searchParams.get("metric") || "",
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
      childcare_id: searchParams.get("childcare_id") || "",
      group_id: searchParams.get("group_id") || "",
    }),
    [searchParams],
  );

  const setParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return { params, setParams };
}
